import { useEffect, useState } from 'react';
import { bridge } from '../lib/bridge';
import { MascotMark } from './Mascot';
import { Doodle, Icon } from './Icon';
import { Popover } from './Overlay';
import { UpdateNotice } from './Updates';
import { language, t } from '../lib/i18n';

/**
 * Title bar: a purple journal-paper strip + sticker buttons (same as the detective app; don't make it look like a system title bar).
 * Clicking the name sticker opens the app menu; on the right: fetch mail, settings, minimize / maximize / close (close = hide to tray).
 */
export type AppSection = 'mail' | 'calendar';

export function TitleBar({
  syncing,
  onSync,
  onSettings,
  onCompose,
  onShortcuts,
  minimal = false,
  section,
  onSection,
}: {
  syncing: boolean;
  onSync: () => void;
  onSettings: () => void;
  onCompose: () => void;
  onShortcuts: () => void;
  /** Login / connect page: only the name and window buttons. */
  minimal?: boolean;
  /** Mail / calendar switch (absent on the login page). */
  section?: AppSection;
  onSection?: (s: AppSection) => void;
}) {
  const controls = bridge.window;
  const [maximized, setMaximized] = useState(false);
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    if (!controls) return;
    let active = true;
    const off = controls.onMaximized(setMaximized);
    void controls.isMaximized().then((v) => active && setMaximized(v));
    return () => {
      active = false;
      off();
    };
  }, [controls]);
  const item = (label: string, icon: Parameters<typeof Icon>[0]['name'], fn: () => void, hint?: string) => (
    <button
      role="menuitem"
      className="popover__item"
      onClick={() => {
        setMenu(false);
        fn();
      }}
    >
      <Icon name={icon} size={16} />
      <span>{label}</span>
      {hint && <kbd>{hint}</kbd>}
    </button>
  );
  return (
    <header className="titlebar" onDoubleClick={(e) => e.target === e.currentTarget && controls?.toggleMaximize()}>
      <div className="titlebar__left">
        <button type="button" data-popover-anchor className="titlebar__name" aria-haspopup="menu" aria-expanded={menu} title={t('馍馍邮箱菜单')} onClick={() => setMenu((v) => !v)}>
          <MascotMark />
          <span className="titlebar__word" aria-label={t('馍馍邮箱')}>
            {Array.from(t('馍馍邮箱')).map((ch, i) => (
              <span key={i} aria-hidden="true" style={{ '--i': i } as React.CSSProperties}>
                {ch}
              </span>
            ))}
          </span>
          {language() === 'zh' && <small className="titlebar__subtitle">momomail</small>}
        </button>
        <Popover open={menu} onClose={() => setMenu(false)} className="titlebar__menu">
          {!minimal && item(t('写邮件'), 'pen', onCompose, 'C')}
          {!minimal && item(t('立即收信'), 'refresh', onSync, 'F5')}
          {!minimal && item(t('设置'), 'sliders', onSettings, 'Ctrl ,')}
          {!minimal && item(t('键盘快捷键'), 'keyboard', onShortcuts, '?')}
          {!minimal && controls && <div className="popover__sep" />}
          {controls && item(t('收进托盘'), 'inbox', () => controls.close())}
          {controls && item(t('退出馍馍邮箱'), 'power', () => bridge.app.quit(), 'Ctrl Q')}
          {!controls && item(t('重新加载'), 'refresh', () => location.reload())}
        </Popover>
        {!minimal && section && onSection && (
          <nav className="titlebar__tabs" aria-label={t('切换邮件和日历')}>
            {(
              [
                ['mail', t('邮件'), 'mail', 'G M'],
                ['calendar', t('日历'), 'calendar', 'G C'],
              ] as const
            ).map(([id, label, icon, keys]) => (
              <button key={id} type="button" className="titlebar__tab" aria-pressed={section === id} title={`${label}（${keys}）`} onClick={() => onSection(id)}>
                <Icon name={icon} size={14} /> {label}
              </button>
            ))}
          </nav>
        )}
      </div>
      <div className="titlebar__controls">
        <UpdateNotice />
        {!minimal && (
          <>
            <button type="button" className={`titlebar__sync ${syncing ? 'is-syncing' : ''}`} aria-label={t('立即收信')} title={t('立即收信（F5）')} onClick={onSync}>
              <span>
                <Doodle name="refresh" />
              </span>
            </button>
            <button type="button" className="titlebar__settings" aria-label={t('设置')} title={t('设置')} onClick={onSettings}>
              <span>
                <Doodle name="settings" />
              </span>
            </button>
          </>
        )}
        {controls && (
          <>
        <i className="titlebar__gap" />
        <button type="button" className="titlebar__min" aria-label={t('最小化')} title={t('最小化')} onClick={() => controls?.minimize()}>
          <span>
            <Doodle name="minimize" />
          </span>
        </button>
        <button type="button" className="titlebar__max" aria-label={maximized ? t('还原') : t('最大化')} title={maximized ? t('还原') : t('最大化')} onClick={() => controls?.toggleMaximize()}>
          <span>
            <Doodle name={maximized ? 'restore' : 'maximize'} />
          </span>
        </button>
        <button type="button" className="titlebar__close" aria-label={t('关闭（收进托盘）')} title={t('关闭（收进托盘，继续收信）')} onClick={() => controls?.close()}>
          <span>
            <Doodle name="close" />
          </span>
        </button>
          </>
        )}
      </div>
    </header>
  );
}
